import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  TextField,
  MenuItem,
  Typography,
  Button,
  CircularProgress,
} from "@mui/material";
import { getAuth } from "firebase/auth";
import { useAuth } from "../../context/AuthContext";
import { formatPhoneNumber } from "../../utils/validation";
import GooglePlacesAutocomplete from "../../components/GooglePlacesAutocomplete";
import extractAddressParts from "../../utils/extractAddressParts";
import api from "../../services/api";
import { useSnackbarAlert } from "../../hooks/useSnackbarAlert";
import { getUTCDateFromParts } from "../../utils/dates/getUTCDateFromParts";
import { getNewfoundlandDateParts } from "../../utils/dates/getNewfoundlandDateParts";

const AccountDetailsForm = () => {
  const { user, isAuthenticated, fetchUserDetails } = useAuth();
  const snackbar = useSnackbarAlert();
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    firstName: user?.firstName || "",
    lastName: user?.lastName || "",
    phoneNumber:
      !user?.phoneNumber || user.phoneNumber.toLowerCase() === "unknown"
        ? ""
        : user.phoneNumber,
    gender: user?.gender || "",
    dob: {
      day: "",
      month: "",
      year: "",
    },
    address: user?.address?.fullAddress || "",
    addressConfirmed: !!user?.address?.fullAddress,
    structuredAddress: user?.address || null,
  });

  useEffect(() => {
    if (!isAuthenticated || !user) return null;

    if (user?.dob) {
      const parts = getNewfoundlandDateParts(user.dob);
      if (parts) {
        setFormData((prev) => ({
          ...prev,
          dob: {
            day: String(parts.day).padStart(2, "0"),
            month: String(parts.month).padStart(2, "0"),
            year: String(parts.year),
          },
        }));
      }
    }
  }, [user?.dob]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleDobChange = (part, value) => {
    setFormData((prev) => ({
      ...prev,
      dob: { ...prev.dob, [part]: value },
    }));
  };

  const handleSave = async () => {
    const {
      firstName,
      lastName,
      phoneNumber,
      gender,
      dob,
      address,
      structuredAddress,
      addressConfirmed,
    } = formData;

    const trimmedAddress = typeof address === "string" ? address.trim() : "";
    const userEnteredAddress = trimmedAddress.length > 0;
    const isStructured =
      structuredAddress && typeof structuredAddress === "object";

    const shouldBlock =
      userEnteredAddress && (!addressConfirmed || !isStructured);

    if (shouldBlock) {
      snackbar.warning("Please select a valid address from the suggestions.");
      return;
    }

    const dobUTC = getUTCDateFromParts({
      year: parseInt(dob.year),
      month: parseInt(dob.month),
      day: parseInt(dob.day),
    });

    const updatedData = {
      firstName,
      lastName,
      phoneNumber,
      gender,
      dob: dobUTC,
      address: structuredAddress || null,
    };

    try {
      setSaving(true);
      await api.patch("/users/me", updatedData); // token handled via Axios
      await fetchUserDetails(); // uses /users/me and Axios token too
      snackbar.success("Your profile has been updated!");
    } catch (err) {
      snackbar.error("Failed to update profile. Please try again later.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            label="First Name"
            value={formData.firstName}
            onChange={(e) => handleChange("firstName", e.target.value)}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Last Name"
            value={formData.lastName}
            onChange={(e) => handleChange("lastName", e.target.value)}
            fullWidth
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            label="Phone Number"
            placeholder="(123) 456-7890"
            value={formData.phoneNumber}
            onChange={(e) =>
              handleChange("phoneNumber", formatPhoneNumber(e.target.value))
            }
            fullWidth
          />
        </Grid>

        {/* DOB */}
        <Grid item xs={12}>
          <Typography
            variant="subtitle2"
            sx={{ mb: 0.5, fontWeight: 500, color: "#1A1A1A" }}>
            Date of Birth
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={4}>
              <TextField
                select
                label="Day"
                value={formData.dob.day}
                onChange={(e) => handleDobChange("day", e.target.value)}
                fullWidth>
                {[...Array(31).keys()].map((d) => (
                  <MenuItem key={d + 1} value={String(d + 1).padStart(2, "0")}>
                    {d + 1}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={4}>
              <TextField
                select
                label="Month"
                value={formData.dob.month}
                onChange={(e) => handleDobChange("month", e.target.value)}
                fullWidth>
                {[...Array(12).keys()].map((i) => (
                  <MenuItem key={i + 1} value={String(i + 1).padStart(2, "0")}>
                    {new Date(0, i).toLocaleString("default", {
                      month: "short",
                    })}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={4}>
              <TextField
                select
                label="Year"
                value={formData.dob.year}
                onChange={(e) => handleDobChange("year", e.target.value)}
                fullWidth>
                {Array.from({ length: 100 }, (_, i) => {
                  const year = new Date().getFullYear() - i;
                  return (
                    <MenuItem key={year} value={String(year)}>
                      {year}
                    </MenuItem>
                  );
                })}
              </TextField>
            </Grid>
          </Grid>
        </Grid>

        {/* Gender */}
        <Grid item xs={12}>
          <TextField
            select
            label="Gender"
            value={formData.gender}
            onChange={(e) => handleChange("gender", e.target.value)}
            fullWidth>
            <MenuItem value="">Prefer not to say</MenuItem>
            <MenuItem value="Male">Male</MenuItem>
            <MenuItem value="Female">Female</MenuItem>
            <MenuItem value="Other">Other</MenuItem>
          </TextField>
        </Grid>

        {/* Address */}
        <Grid item xs={12}>
          <GooglePlacesAutocomplete
            label="Address"
            value={formData.address}
            onChange={(val) =>
              setFormData((prev) => ({
                ...prev,
                address: val,
                addressConfirmed: false,
                structuredAddress: null,
              }))
            }
            onSelect={(label, placeDetails) => {
              const parsed = extractAddressParts(placeDetails);
              setFormData((prev) => ({
                ...prev,
                address: label,
                addressConfirmed: true,
                structuredAddress: parsed,
              }));
            }}
            confirmed={formData.addressConfirmed}
            showWarningIfUnconfirmed={true}
          />
        </Grid>

        <Grid item xs={12}>
          <Button
            variant="contained"
            fullWidth
            sx={{ mt: 2 }}
            onClick={handleSave}
            disabled={saving}>
            {saving ? (
              <>
                Saving...
                <CircularProgress size={16} sx={{ ml: 1, color: "white" }} />
              </>
            ) : (
              "Save Changes"
            )}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AccountDetailsForm;
